import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { useReputationRewards } from '../../../hooks/useReputationRewards';
import { extractGroupIdFromParam, createGroupUrl } from '../../../hooks/useNavigation';
import { supabase } from '../../../lib/supabase';
import Header from '../../../components/feature/Header';
import Footer from '../../home/components/Footer';
import GroupHeader from './components/GroupHeader';
import MembersTab from './components/MembersTab';
import DiscussionFeed from './components/DiscussionFeed';
import AboutTab from './components/AboutTab';
import GroupSettingsModal from './components/GroupSettingsModal';
import GroupActivityFeed from './components/GroupActivityFeed';
import GroupRulesWidget from './components/GroupRulesWidget';
import CreateMomentModal from './components/CreateMomentModal';
import InviteMembersModal from './components/InviteMembersModal';
import WelcomeMessageModal from './components/WelcomeMessageModal';

interface Group {
  id: string;
  sequential_id: number;
  name: string;
  description: string;
  cover_image: string;
  category: string;
  privacy: string;
  owner_id: string;
  member_count: number;
  created_at: string;
  rules: string[];
  welcome_message: string;
}

interface Member {
  id: string;
  user_id: string;
  role: 'owner' | 'admin' | 'moderator' | 'member';
  badge: string | null;
  level: number;
  joined_at: string;
  user_profiles: {
    username: string;
    avatar_url: string;
  };
}

interface Post {
  id: string;
  title: string;
  content: string;
  author_id: string;
  created_at: string;
  likes_count: number;
  comments_count: number;
  is_pinned: boolean;
  user_profiles: {
    username: string;
    avatar_url: string;
  };
}

interface Topic {
  id: string;
  name: string;
}

const GroupDetailPage = () => {
  const { urlParam } = useParams<{ urlParam: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { awardPoints } = useReputationRewards();
  
  // Extract sequential ID from URL parameter (format: group-001-slug)
  const sequentialId = extractGroupIdFromParam(urlParam);
  
  const [group, setGroup] = useState<Group | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [activeTab, setActiveTab] = useState<'discussions' | 'members' | 'about'>('discussions');
  const [isMember, setIsMember] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState<'general' | 'topics' | 'rules' | 'welcome' | 'members' | 'danger'>('general');
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // Check membership status - this runs whenever user or members change
  const checkMembershipStatus = useCallback(() => {
    if (user && members.length > 0) {
      const userMember = members.find((m: Member) => m.user_id === user.id);
      const memberStatus = !!userMember;
      const role = userMember?.role || null;
      
      console.log('✅ Membership check:', { 
        userId: user.id, 
        memberStatus, 
        role, 
        membersCount: members.length,
        userMemberFound: !!userMember 
      });
      
      setIsMember(memberStatus);
      setUserRole(role);
    } else if (!user) {
      console.log('❌ No user logged in');
      setIsMember(false);
      setUserRole(null);
    } else if (user && members.length === 0) {
      console.log('⚠️ User logged in but no members loaded yet');
    }
  }, [user, members]);

  // Run membership check whenever dependencies change
  useEffect(() => {
    checkMembershipStatus();
  }, [checkMembershipStatus]);

  const loadGroup = useCallback(async (isRefresh = false) => {
    if (!sequentialId) {
      setError('Invalid group URL');
      setLoading(false);
      return;
    }
    
    try {
      // Only show full loading spinner on initial load, not refreshes
      if (!isRefresh) {
        setLoading(true);
      }
      setError(null);

      console.log('🔄 Loading group data for sequential_id:', sequentialId);

      // Load group data by sequential_id
      const { data: groupData, error: groupError } = await supabase
        .from('groups')
        .select('*')
        .eq('sequential_id', sequentialId)
        .maybeSingle();

      if (groupError) {
        console.error('Error loading group:', groupError);
        setError('Failed to load group');
        setLoading(false);
        return;
      }

      if (!groupData) {
        setError('Group not found');
        setLoading(false);
        return;
      }

      console.log('✅ Group loaded:', groupData.name);
      setGroup(groupData);

      // Generate correct URL from group data
      const correctUrl = createGroupUrl(groupData.sequential_id, groupData.name);
      const correctUrlParam = correctUrl.split('/groups/')[1];
      
      // Redirect if URL doesn't match
      if (urlParam !== correctUrlParam) {
        navigate(correctUrl, { replace: true });
        return;
      }

      // Load members with user profiles - CRITICAL: This must complete before membership check
      console.log('🔄 Loading members for group:', groupData.id);
      const { data: membersData, error: membersError } = await supabase
        .from('group_members')
        .select(`
          *,
          user_profiles (
            username,
            avatar_url
          )
        `)
        .eq('group_id', groupData.id)
        .order('joined_at', { ascending: false });

      if (!membersError && membersData) {
        console.log('✅ Members loaded:', membersData.length);
        setMembers(membersData);
        
        // IMMEDIATE membership check after setting members
        if (user) {
          const userMember = membersData.find((m: any) => m.user_id === user.id);
          if (userMember) {
            console.log('✅ User is a member! Role:', userMember.role);
            setIsMember(true);
            setUserRole(userMember.role);
          } else {
            console.log('❌ User is NOT a member');
            setIsMember(false);
            setUserRole(null);
          }
        }
      } else {
        console.error('Error loading members:', membersError);
      }

      // Load posts with user profiles
      const { data: postsData, error: postsError } = await supabase
        .from('posts')
        .select(`
          *,
          user_profiles (
            username,
            avatar_url
          )
        `)
        .eq('group_id', groupData.id)
        .order('is_pinned', { ascending: false })
        .order('created_at', { ascending: false });

      if (!postsError && postsData) {
        setPosts(postsData);
      }

      // Load topics
      const { data: topicsData, error: topicsError } = await supabase
        .from('group_topics')
        .select('*')
        .eq('group_id', groupData.id)
        .order('name');

      if (!topicsError && topicsData) {
        setTopics(topicsData);
      }
    } catch (err) {
      console.error('Error loading group:', err);
      setError('Failed to load group. Please try again.');
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
    }
  }, [sequentialId, urlParam, navigate, user]);

  // Initial load
  useEffect(() => {
    loadGroup();
  }, [loadGroup]);

  // Re-check membership when user signs in after page already loaded
  useEffect(() => {
    if (user && initialLoadDone && group && members.length === 0) {
      console.log('🔄 User signed in after page load, re-fetching members');
      // Re-fetch members to check membership for newly signed-in user
      const checkMembership = async () => {
        const { data: membersData } = await supabase
          .from('group_members')
          .select(`
            *,
            user_profiles (
              username,
              avatar_url
            )
          `)
          .eq('group_id', group.id)
          .order('joined_at', { ascending: false });

        if (membersData) {
          console.log('✅ Re-fetched members:', membersData.length);
          setMembers(membersData);
        }
      };
      checkMembership();
    }
  }, [user?.id, initialLoadDone, group?.id, members.length]);

  const handleJoinLeave = async () => {
    if (!user || !group) return;

    try {
      setActionLoading(true);
      
      if (isMember) {
        // Leave group
        console.log('🚪 Leaving group...');
        const { error: leaveError } = await supabase
          .from('group_members')
          .delete()
          .eq('group_id', group.id)
          .eq('user_id', user.id);

        if (leaveError) throw leaveError;

        // Update member count
        await supabase
          .from('groups')
          .update({ member_count: Math.max(0, group.member_count - 1) })
          .eq('id', group.id);

        // Update state immediately - CRITICAL ORDER
        setIsMember(false);
        setUserRole(null);
        const updatedMembers = members.filter(m => m.user_id !== user.id);
        setMembers(updatedMembers);
        setGroup(prev => prev ? { ...prev, member_count: Math.max(0, prev.member_count - 1) } : prev);
        
        console.log('✅ Left group - states updated:', { isMember: false, userRole: null, membersCount: updatedMembers.length });
        
        showNotification(`Left ${group.name}`, 'info');
      } else {
        // Join group - insert without nested query
        console.log('🚪 Joining group...');
        const { data: newMemberData, error: joinError } = await supabase
          .from('group_members')
          .insert({
            group_id: group.id,
            user_id: user.id,
            role: 'member',
            badge: null,
            level: 1,
            has_seen_welcome: false,
          })
          .select()
          .single();

        if (joinError) throw joinError;

        // Fetch user profile separately
        const { data: userProfile } = await supabase
          .from('user_profiles')
          .select('username, avatar_url')
          .eq('id', user.id)
          .single();

        // Update member count
        await supabase
          .from('groups')
          .update({ member_count: group.member_count + 1 })
          .eq('id', group.id);

        // Update state immediately - CRITICAL ORDER: Set membership FIRST, then update members list
        setIsMember(true);
        setUserRole('member');
        
        if (newMemberData && userProfile) {
          const newMember = {
            ...newMemberData,
            user_profiles: userProfile
          };
          const updatedMembers = [newMember, ...members];
          setMembers(updatedMembers);
          
          console.log('✅ Joined group - states updated:', { 
            isMember: true, 
            userRole: 'member',
            membersCount: updatedMembers.length 
          });
        }
        setGroup(prev => prev ? { ...prev, member_count: prev.member_count + 1 } : prev);
        
        // Award reputation points for joining a group
        const result = await awardPoints(user.id, 'join_group');
        if (result.success) {
          showNotification(`Joined ${group.name}! +${result.pointsAwarded} points`, 'success');
        } else {
          showNotification(`Joined ${group.name}!`, 'success');
        }

        // Show welcome message if one is set
        if (group.welcome_message && group.welcome_message.trim()) {
          setTimeout(() => setShowWelcomeModal(true), 600);
        }
      }

      // Refresh data in background without showing loading spinner
      setTimeout(() => loadGroup(true), 500);
    } catch (err: any) {
      console.error('Error joining/leaving group:', err);
      showNotification(err.message || 'Action failed. Please try again.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    if (!group) return;
    
    try {
      setActionLoading(true);
      const { error } = await supabase
        .from('group_members')
        .update({ role: newRole })
        .eq('group_id', group.id)
        .eq('user_id', userId);

      if (error) throw error;

      showNotification('Role updated successfully', 'success');
      loadGroup();
    } catch (err: any) {
      console.error('Error changing role:', err);
      showNotification(err.message || 'Failed to change role', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!group) return;
    if (!window.confirm('Are you sure you want to remove this member?')) return;

    try {
      setActionLoading(true);
      const { error } = await supabase
        .from('group_members')
        .delete()
        .eq('group_id', group.id)
        .eq('user_id', userId);

      if (error) throw error;

      // Update member count
      await supabase
        .from('groups')
        .update({ member_count: Math.max(0, group.member_count - 1) })
        .eq('id', group.id);

      showNotification('Member removed successfully', 'success');
      loadGroup();
    } catch (err: any) {
      console.error('Error removing member:', err);
      showNotification(err.message || 'Failed to remove member', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveSettings = async (data: any) => {
    if (!group) return;
    
    try {
      setActionLoading(true);
      const { error } = await supabase
        .from('groups')
        .update({
          name: data.name,
          description: data.description,
          category: data.category,
          privacy: data.privacy,
          rules: data.rules,
          welcome_message: data.welcome_message || '',
        })
        .eq('id', group.id);

      if (error) throw error;

      showNotification('Settings saved successfully', 'success');
      loadGroup();
    } catch (err: any) {
      console.error('Error saving settings:', err);
      showNotification(err.message || 'Failed to save settings', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!group) return;
    
    try {
      setActionLoading(true);
      const { error } = await supabase
        .from('groups')
        .delete()
        .eq('id', group.id);

      if (error) throw error;

      showNotification('Group deleted successfully', 'success');
      navigate('/groups');
    } catch (err: any) {
      console.error('Error deleting group:', err);
      showNotification(err.message || 'Failed to delete group', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePinPost = async (postId: string) => {
    if (!group) return;
    
    try {
      const post = posts.find((p) => p.id === postId);
      const { error } = await supabase
        .from('posts')
        .update({ is_pinned: !post?.is_pinned })
        .eq('id', postId);

      if (error) throw error;

      showNotification(post?.is_pinned ? 'Post unpinned' : 'Post pinned', 'success');
      loadGroup();
    } catch (err: any) {
      console.error('Error pinning post:', err);
      showNotification(err.message || 'Failed to pin/unpin post', 'error');
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!group) return;
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const { error } = await supabase
        .from('posts')
        .delete()
        .eq('id', postId);

      if (error) throw error;

      showNotification('Post deleted successfully', 'success');
      loadGroup();
    } catch (err: any) {
      console.error('Error deleting post:', err);
      showNotification(err.message || 'Failed to delete post', 'error');
    }
  };

  const handleCreatePost = () => {
    // No-op: inline composer handles creation directly
  };

  const handleMomentCreated = () => {
    loadGroup(true);
  };

  const handleOpenSettings = (tab?: 'general' | 'topics' | 'rules' | 'welcome' | 'members' | 'danger') => {
    setSettingsInitialTab(tab || 'general');
    setIsSettingsOpen(true);
  };

  const handleDismissWelcome = async () => {
    if (!user || !group) return;
    try {
      await supabase
        .from('group_members')
        .update({ has_seen_welcome: true })
        .eq('group_id', group.id)
        .eq('user_id', user.id);
    } catch (err) {
      console.error('Error dismissing welcome:', err);
    }
  };

  const showNotification = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const bgColor = type === 'success' 
      ? 'bg-gradient-to-r from-emerald-500 to-teal-500' 
      : type === 'error' 
        ? 'bg-gradient-to-r from-red-500 to-rose-500'
        : 'bg-gradient-to-r from-blue-500 to-indigo-500';
    
    const notification = document.createElement('div');
    notification.className = `fixed bottom-4 right-4 ${bgColor} text-white px-4 py-2 rounded-lg shadow-lg z-50 transition-all duration-300`;
    notification.innerHTML = `<i class="ri-${type === 'success' ? 'check' : type === 'error' ? 'error-warning' : 'information'}-line mr-2"></i>${message}`;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.classList.add('opacity-0', 'translate-y-2');
      setTimeout(() => notification.remove(), 300);
    }, 2500);
  };

  // Check if user needs to see welcome message on initial load
  useEffect(() => {
    if (
      initialLoadDone &&
      user &&
      group &&
      group.welcome_message &&
      group.welcome_message.trim() &&
      isMember &&
      members.length > 0
    ) {
      const userMember = members.find(m => m.user_id === user.id);
      if (userMember && !(userMember as any).has_seen_welcome) {
        setShowWelcomeModal(true);
      }
    }
  }, [initialLoadDone, user, group?.id, isMember, members]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading group...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !group) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <div className="flex-1 max-w-7xl mx-auto px-4 py-12 text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-4xl text-gray-400"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {error || 'Group Not Found'}
          </h2>
          <p className="text-gray-600 mb-6">
            {error ? 'Please try again later.' : "The group you're looking for doesn't exist or has been removed."}
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => loadGroup()}
              className="inline-flex items-center gap-2 px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              Try Again
            </button>
            <Link
              to="/groups"
              className="inline-flex items-center gap-2 px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Groups
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const stats = {
    totalPosts: posts.length,
    totalMembers: members.length,
    activeToday: Math.floor(members.length * 0.15),
  };

  const isOwnerOrAdmin = userRole === 'owner' || userRole === 'admin';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <div className="flex-1 max-w-7xl mx-auto px-4 py-6 w-full">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-600 mb-4">
          <Link to="/" className="hover:text-teal-600 transition-colors">Home</Link>
          <i className="ri-arrow-right-s-line text-gray-400"></i>
          <Link to="/groups" className="hover:text-teal-600 transition-colors">Groups</Link>
          <i className="ri-arrow-right-s-line text-gray-400"></i>
          <span className="text-gray-900 font-medium truncate max-w-xs">{group.name}</span>
        </nav>

        {/* Back to Groups Button */}
        <Link
          to="/groups"
          className="inline-flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors mb-4 cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          <span className="font-medium">Back to Groups</span>
        </Link>

        <GroupHeader
          group={group}
          isMember={isMember}
          userRole={userRole}
          currentUserId={user?.id || null}
          onJoinLeave={handleJoinLeave}
          onOpenSettings={() => handleOpenSettings('general')}
          onOpenInvite={() => setIsInviteModalOpen(true)}
          isLoading={actionLoading}
        />

        <div className="mt-6 flex border-b border-gray-200 bg-white rounded-t-lg">
          <button
            onClick={() => setActiveTab('discussions')}
            className={`px-6 py-3 font-medium transition-colors cursor-pointer ${
              activeTab === 'discussions'
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <i className="ri-chat-3-line mr-2"></i>
            Discussions
          </button>
          <button
            onClick={() => setActiveTab('members')}
            className={`px-6 py-3 font-medium transition-colors cursor-pointer ${
              activeTab === 'members'
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <i className="ri-group-line mr-2"></i>
            Members ({group.member_count})
          </button>
          <button
            onClick={() => setActiveTab('about')}
            className={`px-6 py-3 font-medium transition-colors cursor-pointer ${
              activeTab === 'about'
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <i className="ri-information-line mr-2"></i>
            About
          </button>
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'discussions' && (
              <>
                {isMember && (
                  <div className="mb-4">
                    <CreateMomentModal
                      groupId={group.id}
                      groupName={group.name}
                      onMomentCreated={handleMomentCreated}
                      inline
                    />
                  </div>
                )}
                <DiscussionFeed
                  key={`discussions-${isMember}-${user?.id || 'guest'}`}
                  posts={posts}
                  groupId={group.id}
                  currentUserId={user?.id || null}
                  userRole={userRole}
                  members={members}
                  isMember={isMember}
                  onCreatePost={handleCreatePost}
                  onPinPost={handlePinPost}
                  onDeletePost={handleDeletePost}
                  onRefresh={loadGroup}
                />
              </>
            )}

            {activeTab === 'members' && (
              <MembersTab
                members={members}
                currentUserRole={userRole}
                groupId={group.id}
                onRoleChange={handleRoleChange}
                onRemoveMember={handleRemoveMember}
                onRefresh={loadGroup}
              />
            )}

            {activeTab === 'about' && (
              <AboutTab
                description={group.description}
                rules={group.rules || []}
                topics={topics}
                createdAt={group.created_at}
                stats={stats}
                isOwnerOrAdmin={isOwnerOrAdmin}
                onEditRules={() => handleOpenSettings('rules')}
              />
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Welcome Message Widget for owners/admins */}
            {isOwnerOrAdmin && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-chat-smile-3-line text-teal-500"></i>
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm">Welcome Message</h3>
                  </div>
                  <button
                    onClick={() => handleOpenSettings('welcome')}
                    className="text-xs text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap"
                  >
                    Edit
                  </button>
                </div>
                <div className="p-4">
                  {group.welcome_message && group.welcome_message.trim() ? (
                    <div>
                      <div className="flex items-center gap-1.5 mb-2">
                        <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                        <span className="text-xs font-medium text-emerald-600">Active</span>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-3 leading-relaxed">{group.welcome_message}</p>
                    </div>
                  ) : (
                    <div className="text-center py-2">
                      <p className="text-xs text-gray-500 mb-2">No welcome message set</p>
                      <button
                        onClick={() => handleOpenSettings('welcome')}
                        className="text-xs text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-add-line mr-0.5"></i>
                        Add one
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Group Rules Widget */}
            <GroupRulesWidget
              rules={group.rules || []}
              isOwnerOrAdmin={isOwnerOrAdmin}
              onOpenSettings={() => handleOpenSettings('rules')}
            />

            {/* Invite Members Quick Card */}
            {isMember && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-user-add-line text-teal-500"></i>
                  </div>
                  <h3 className="font-semibold text-gray-900">Grow Your Group</h3>
                </div>
                <div className="p-4">
                  <p className="text-sm text-gray-600 mb-3">Invite friends and community members to join the conversation.</p>
                  <button
                    onClick={() => setIsInviteModalOpen(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-500 hover:bg-teal-600 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-user-add-line"></i>
                    Invite Members
                  </button>
                </div>
              </div>
            )}

            {/* Activity Feed */}
            <GroupActivityFeed
              groupId={group.id}
              members={members}
              posts={posts}
            />
          </div>
        </div>
      </div>

      <Footer />

      {isSettingsOpen && (
        <GroupSettingsModal
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          group={group}
          onSave={handleSaveSettings}
          onDelete={handleDeleteGroup}
          userRole={userRole || 'member'}
          initialTab={settingsInitialTab}
        />
      )}

      {isInviteModalOpen && (
        <InviteMembersModal
          isOpen={isInviteModalOpen}
          onClose={() => setIsInviteModalOpen(false)}
          groupId={group.id}
          groupName={group.name}
          groupUrl={`${window.location.origin}${createGroupUrl(group.sequential_id, group.name)}`}
          groupDescription={group.description}
          existingMemberIds={members.map(m => m.user_id)}
          onMemberInvited={() => loadGroup(true)}
        />
      )}

      {showWelcomeModal && group.welcome_message && (
        <WelcomeMessageModal
          isOpen={showWelcomeModal}
          onClose={() => setShowWelcomeModal(false)}
          groupName={group.name}
          welcomeMessage={group.welcome_message}
          groupCoverImage={group.cover_image}
          onDismiss={handleDismissWelcome}
        />
      )}
    </div>
  );
};

export default GroupDetailPage;
